import React, { useState, useEffect } from 'react';
import useWalletData from '../../../hooks/useWalletData';
import useUserPermissions from '../../../hooks/useUserPermissions';
import StatCard from '../../common/StatCard/StatCard';
import WalletCard from '../../common/WalletCard/WalletCard';
import { formatCurrency, formatPercent } from '../../../utils/formatters';
import api from '../../../utils/api';
import './WalletsOverview.css';

/**
 * WalletsOverview - Vista principal de wallets modernizada
 */
export default function WalletsOverview() {
  const { isAdmin } = useUserPermissions();
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { data, loading, syncWallets, totalValueUsd, totalProfit, profitPercentage } = useWalletData(selectedUser, true, 30000);
  const [syncing, setSyncing] = useState(false);

  // Fetch users list for admin
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAdmin) return;

      setLoadingUsers(true);
      try {
        const usersList = await api.getAllUsers();
        setUsers(usersList);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [isAdmin]);

  const handleSync = async () => {
    setSyncing(true);
    await syncWallets();
    setSyncing(false);
  };

  return (
    <div className="wallets-overview">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">👛 My Wallets</h1>
          <p className="page-subtitle">All your crypto wallets in one place</p>
        </div>
        
        <button 
          className="btn btn-primary"
          onClick={handleSync}
          disabled={syncing || loading}
        >
          {syncing ? '⏳ Syncing...' : '🔄 Sync Wallets'}
        </button>
      </div>

      {/* Admin User Selector */}
      {isAdmin && (
        <div className="user-selector-container">
          <label htmlFor="user-select">Select User:</label>
          <select
            id="user-select"
            value={selectedUser || ''}
            onChange={(e) => setSelectedUser(e.target.value || null)}
            className="user-select"
            disabled={loadingUsers}
          >
            <option value="">All Users</option>
            {loadingUsers ? (
              <option disabled>Loading users...</option>
            ) : (
              users.map(user => (
                <option key={user.id || user._id} value={user.id || user._id}>
                  {user.nombre || user.name || user.username || `User ${user.id || user._id}`}
                </option>
              ))
            )}
          </select>
          {loadingUsers && <span className="loading-text">⏳</span>}
        </div>
      )}

      {loading && !data ? (
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading wallets...</p>
        </div>
      ) : (
        <>
          {/* Total Portfolio Value */}
          <div className="total-value-section">
            <div className="stat-grid grid grid-cols-3">
              <StatCard
                title="Total Portfolio Value"
                value={formatCurrency(totalValueUsd)}
                icon="💰"
                className="total-card"
              />
              
              <StatCard
                title="Total Deposits"
                value={formatCurrency(data?.totalDeposits || 0)}
                icon="📥"
              />
              
              <StatCard
                title="Total Profit"
                value={formatCurrency(totalProfit)}
                change={formatPercent(profitPercentage)}
                trend={profitPercentage >= 0 ? 'up' : 'down'}
                icon="📈"
              />
            </div>
          </div>

          {/* Wallet Cards */}
          <div className="wallets-grid grid grid-cols-3">
            {/* Binance Spot */}
            <WalletCard
              type="spot"
              title="Binance Spot"
              subtitle="Grid Bot"
              balance={data?.binanceSpot?.totalUsd || 0}
              assets={(data?.binanceSpot?.balances || []).map(bal => ({
                symbol: bal.asset,
                amount: bal.total,
                decimals: bal.asset === 'BTC' ? 8 : (bal.asset === 'USDT' || bal.asset === 'USDC' ? 2 : 4)
              }))}
            />

            {/* Binance Futures */}
            <WalletCard
              type="futures"
              title="Binance Futures"
              subtitle="Margin Trading"
              balance={data?.binanceFutures?.totalWalletBalance || 0}
              stats={[
                {
                  label: 'Available',
                  value: formatCurrency(data?.binanceFutures?.availableBalance || 0)
                },
                {
                  label: 'In Positions',
                  value: formatCurrency(data?.binanceFutures?.totalPositionInitialMargin || 0)
                },
                {
                  label: 'Unrealized PnL',
                  value: formatCurrency(data?.binanceFutures?.totalUnrealizedProfit || 0),
                  className: (data?.binanceFutures?.totalUnrealizedProfit || 0) >= 0 ? 'profit' : 'loss'
                }
              ]}
            />

            {/* Solana */}
            <WalletCard
              type="solana"
              title="Solana Wallet"
              subtitle="SPL Tokens"
              balance={data?.solana?.totalUsd || 0}
              assets={[
                {
                  symbol: 'SOL',
                  amount: data?.solana?.solValueUsd || 0,
                  decimals: 2,
                  isUsd: true
                },
                ...(data?.solana?.tokens || []).map(token => ({
                  symbol: token.symbol,
                  amount: token.usdValue || 0,
                  decimals: 2,
                  isUsd: true
                }))
              ]}
            />
          </div>

          {/* Portfolio Distribution */}
          {data && totalValueUsd > 0 && (
            <div className="distribution-section card">
              <h3 className="card-title">💼 Portfolio Distribution</h3>
              
              <div className="distribution-pie-container">
                {(() => {
                  const total = totalValueUsd || 1;
                  const spotValue = data.binanceSpot?.totalUsd || 0;
                  const futuresValue = data.binanceFutures?.totalWalletBalance || 0;
                  const solanaValue = data.solana?.totalUsd || 0;
                  
                  const spotPct = (spotValue / total) * 100;
                  const futuresPct = (futuresValue / total) * 100;
                  const solanaPct = (solanaValue / total) * 100;

                  // Calculate cumulative percentages for pie slices
                  let cumulativePct = 0;
                  const slices = [
                    { label: 'Binance Spot', icon: '🟠', value: spotValue, pct: spotPct, color: '#F59E0B', start: cumulativePct },
                    { label: 'Binance Futures', icon: '🔵', value: futuresValue, pct: futuresPct, color: '#3B82F6', start: (cumulativePct += spotPct) },
                    { label: 'Solana', icon: '🟢', value: solanaValue, pct: solanaPct, color: '#10B981', start: (cumulativePct += futuresPct) }
                  ].filter(s => s.value > 0);

                  return (
                    <div className="pie-chart-wrapper">
                      <svg viewBox="0 0 200 200" className="pie-chart">
                        {slices.map((slice, idx) => {
                          const startAngle = (slice.start / 100) * 360;
                          const endAngle = ((slice.start + slice.pct) / 100) * 360;
                          
                          const startRad = (startAngle - 90) * (Math.PI / 180);
                          const endRad = (endAngle - 90) * (Math.PI / 180);
                          
                          const x1 = 100 + 80 * Math.cos(startRad);
                          const y1 = 100 + 80 * Math.sin(startRad);
                          const x2 = 100 + 80 * Math.cos(endRad);
                          const y2 = 100 + 80 * Math.sin(endRad);
                          
                          const largeArc = slice.pct > 50 ? 1 : 0;
                          
                          const path = `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`;
                          
                          return (
                            <path
                              key={idx}
                              d={path}
                              fill={slice.color}
                              stroke="#fff"
                              strokeWidth="2"
                            />
                          );
                        })}
                      </svg>
                      
                      <div className="pie-legend">
                        {slices.map((slice, idx) => (
                          <div key={idx} className="legend-item">
                            <span className="legend-color" style={{ backgroundColor: slice.color }}></span>
                            <span className="legend-label">{slice.icon} {slice.label}</span>
                            <span className="legend-value">{formatCurrency(slice.value)} ({slice.pct.toFixed(1)}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
